import { useEffect, useState } from "react";
import { RatingResponseDto, Profile } from "../../../types/types";
import { StarIcon } from "@heroicons/react/24/solid";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

type RatingsProps = {
  member: Profile;
  ratings: RatingResponseDto[];
  currentPage?: number;
  totalPages?: number;
  totalElements?: number;
  isLoading?: boolean;
  onPageChange?: (page: number) => void;
};

const Ratings = ({
  member,
  ratings: incomingRatings,
  currentPage = 0,
  totalPages = 1,
  totalElements,
  isLoading = false,
  onPageChange,
}: RatingsProps) => {
  const { t } = useTranslation("publicProfile");
  const navigate = useNavigate();
  const location = useLocation();
  const [ratings, setRatings] = useState<RatingResponseDto[]>([]);
  const [animatedValues, setAnimatedValues] = useState<Record<number, number>>(
    {},
  );

  useEffect(() => {
    setRatings(incomingRatings);
    setAnimatedValues({});
    incomingRatings.forEach((item, index) => {
      setTimeout(() => animateRating(item.id, item.rating), 150 + index * 100);
    });
  }, [incomingRatings]);

  const animateRating = (id: number, finalValue: number) => {
    let current = 0;
    const duration = 800;
    const steps = 40;
    const increment = finalValue / steps;
    const intervalDelay = duration / steps;

    const timer = setInterval(() => {
      current += increment;
      if (current >= finalValue) {
        current = finalValue;
        clearInterval(timer);
      }

      setAnimatedValues((prev) => ({
        ...prev,
        [id]: Number(current.toFixed(1)),
      }));
    }, intervalDelay);
  };

  const truncateName = (
    firstName: string,
    lastName: string,
    maxLength = 24,
  ) => {
    const full = `${firstName} ${lastName}`.trim();
    if (full.length <= maxLength) return full;

    const truncated = full.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(" ");

    return (
      (lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated) + "…"
    );
  };

  const truncateFeedback = (text: string, maxLength = 5000) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "…";
  };

  const getInitials = (firstName: string, lastName: string) => {
    const f = firstName?.trim()?.[0] ?? "";
    const l = lastName?.trim()?.[0] ?? "";
    return (f + l).toUpperCase();
  };

  const getAvatarClass = (_name: string) => "bg-gray-200 text-gray-600";

  const reviewCount = totalElements ?? ratings.length;
  const canPaginate = Boolean(onPageChange) && totalPages > 1;
  const paginationStart = Math.max(
    0,
    Math.min(currentPage - 1, totalPages - 3),
  );

  const visiblePages = Array.from(
    { length: Math.min(3, totalPages) },
    (_, index) => paginationStart + index,
  );

  const handlePageChange = (page: number) => {
    if (
      !onPageChange ||
      isLoading ||
      page === currentPage ||
      page < 0 ||
      page >= totalPages
    ) {
      return;
    }

    onPageChange(page);
  };

  const navigateToProfile = (profileId?: string | number | null) => {
    if (!profileId) return;
    const profilePath =
      location.pathname === "/view-profile" ? "/view-profile" : "/profile";
    navigate(`${profilePath}?profileId=${profileId}`);
  };

  if (ratings.length === 0) {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-base font-semibold text-gray-800">
            {t("ratings_reviews")}
          </h2>
        </div>

        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
            <StarIcon className="w-6 h-6 text-gray-300" />
          </div>

          <p className="text-gray-400 text-sm">{t("ratings_tab_info")}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-800">
          {t("ratings_reviews")}
        </h2>

        <span className="text-xs text-gray-400">
          {reviewCount}{" "}
          {reviewCount === 1
            ? t("ratings").slice(0, -1) || "review"
            : t("ratings") || "reviews"}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {ratings.map((rating, index) => {
          const animVal = animatedValues[rating.id] ?? 0;
          const fullName = `${rating.profile.firstName ?? ""} ${
            rating.profile.lastName ?? ""
          }`.trim();

          const displayName = truncateName(
            rating.profile.firstName ?? "",
            rating.profile.lastName ?? "",
          );

          const initials = getInitials(
            rating.profile.firstName ?? "",
            rating.profile.lastName ?? "",
          );

          const avatarClass = getAvatarClass(fullName);
          const feedback = truncateFeedback(rating.feedback ?? "");

          return (
            <div
              key={rating.id}
              onClick={() => navigateToProfile(rating.profile.id)}
              className="group bg-white border border-gray-200 rounded-xl p-4 cursor-pointer hover:border-gray-300 hover:bg-gray-50 active:scale-[0.99] transition-all duration-150"
              style={{
                animation: `fadeInUp 0.35s ease-out ${index * 0.06}s both`,
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  {rating.profile.profileImage ? (
                    <img
                      src={rating.profile.profileImage}
                      alt={displayName}
                      className="w-9 h-9 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center font-medium text-sm shrink-0 ${avatarClass}`}
                    >
                      {initials}
                    </div>
                  )}

                  <p
                    className="font-semibold text-black text-sm truncate"
                    title={fullName}
                  >
                    {displayName}
                  </p>
                </div>

                {rating.createdAt && (
                  <p className="text-xs text-gray-400 shrink-0 ml-4">
                    {new Date(rating.createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </p>
                )}
              </div>

              <div className="pl-[48px] flex items-center gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => (
                  <StarIcon
                    key={i}
                    className={`w-3.5 h-3.5 transition-colors duration-300 ${
                      i < Math.floor(animVal)
                        ? "text-amber-400 fill-amber-400"
                        : "text-gray-200 fill-gray-200"
                    }`}
                  />
                ))}
              </div>

              {feedback && (
                <p className="pl-[48px] text-gray-500 text-xs leading-relaxed line-clamp-3 text-left italic">
                  {feedback}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {canPaginate && (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
          <p className="text-xs text-gray-500">
            {t("page")} {currentPage + 1} {t("of")} {totalPages}
          </p>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0 || isLoading}
              className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Previous ratings page"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>

            {visiblePages.map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => handlePageChange(page)}
                disabled={isLoading}
                className={`h-8 min-w-8 px-2 rounded-md text-xs font-medium border ${
                  page === currentPage
                    ? "border-red-500 bg-red-500 text-white"
                    : "border-gray-200 text-gray-700 hover:bg-gray-50"
                } disabled:cursor-not-allowed`}
              >
                {page + 1}
              </button>
            ))}

            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage + 1 >= totalPages || isLoading}
              className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Next ratings page"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(12px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </div>
  );
};

export default Ratings;
